import React, { createContext, useContext, useState, useEffect } from 'react';

type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeContextType {
  isDarkMode: boolean;
  themeMode: ThemeMode;
  toggleDarkMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
const THEME_MODE_KEY = 'backr_theme_mode';
const LEGACY_DARK_MODE_KEY = 'backr_dark_mode';

function getInitialThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';

  const savedMode = localStorage.getItem(THEME_MODE_KEY);
  if (savedMode === 'system' || savedMode === 'light' || savedMode === 'dark') {
    return savedMode;
  }

  // Backward compatibility for older boolean preference key.
  const legacy = localStorage.getItem(LEGACY_DARK_MODE_KEY);
  if (legacy === 'true') return 'dark';
  if (legacy === 'false') return 'light';

  return 'system';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialThemeMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const isDarkMode = themeMode === 'dark' || (themeMode === 'system' && systemPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };

    // Safari fallback for older MediaQueryList API.
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    localStorage.setItem(THEME_MODE_KEY, themeMode);
    localStorage.setItem(LEGACY_DARK_MODE_KEY, isDarkMode.toString());
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [themeMode, isDarkMode]);

  const toggleDarkMode = () => {
    const effectiveDark = themeMode === 'dark' || (themeMode === 'system' && systemPrefersDark);
    setThemeMode(effectiveDark ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, themeMode, toggleDarkMode, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
